import React, { useState, useEffect } from 'react';
import {
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink
} from 'reactstrap';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { GroupDetailsTab } from './tabs/GroupDetailsTab';
import { history, store } from '../../../../store/store';
import { GroupEditForm } from './forms/edit/GroupEditForm';
import { GroupUsersTab } from './tabs/GroupUsersTab';
import { GroupPermissionsTab } from './tabs/GroupPermissionsTab';
import { ACL } from '../../../../config/constants';
import { hideNotification } from '../../../../store/actions/notification';
import { NOTIFICATION_ERROR } from '../../../../store/actions/types';

function GroupDetailsComponent(props) {
  const {
    group,
    updateGroup,
    usersInGroup,
    usersNotInGroup,
    updateGroupWithUser,
    permissionsInGroup,
    permissionsNotInGroup,
    updatePermissions,
    submitPermissions
  } = props;
  const [ activeTab, setActiveTab ] = useState('group');
  const [ tabs, setTabs ] = useState({ group: false });
  const intl = useIntl();
  const hasUsersTabAccess = ACL.ADMIN.USERS.LIST.hasAccess();
  const hasPermissionsTabAccess = ACL.ADMIN.GROUPS.LIST_ACLS.hasAccess();
  const availableTabs = [
    'group',
    ...(hasUsersTabAccess ? [ 'users' ] : []),
    ...(hasPermissionsTabAccess ? [ 'acls' ] : [])
  ];
  const focusableTab = availableTabs.includes(activeTab) ? activeTab : 'group';

  useEffect(() => {
    const tab = new URLSearchParams(props.location.search).get('tab');

    if (tab) {
      if (activeTab !== tab) {
        setActiveTab(tab);
      } else {
        resetEditMode();
      }
    }

    return () => {
      if (store.getState().notification.type === NOTIFICATION_ERROR) {
        store.dispatch(hideNotification());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (tab) => {
    if (activeTab !== tab) {
      history.push({
        search: '?' + new URLSearchParams({ tab }).toString(),
      });

      store.dispatch(hideNotification());
      setActiveTab(tab);
    }
  };

  const resetEditMode = () => {
    const tabsU = tabs;
    let shouldUpdate = false;

    Object.keys(tabsU).map((key) => {
      if (activeTab !== key && tabsU[key] === true) {
        tabsU[key] = false;
        shouldUpdate = true;
      }

      return true;
    });

    if (shouldUpdate) {
      setTabs(tabsU);
    }
  };

  const toggleEditMode = (target, value) => {
    setTabs({
      ...tabs,
      [target]: value,
    });
  };

  const handleTabListKeyDown = (event) => {
    const tabButtons = Array.from(
      event.currentTarget.querySelectorAll('[role="tab"]:not(:disabled)')
    );
    const currentIndex = tabButtons.indexOf(document.activeElement);
    let nextIndex;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabButtons.length - 1;
    } else if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabButtons.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabButtons.length) % tabButtons.length;
    } else {
      return;
    }

    event.preventDefault();
    tabButtons[nextIndex].focus();
  };

  return (
    <div className="admin-group-detail-tabs">
      <Nav
        aria-label={intl.formatMessage({ id: 'pages.admin.view_group.title' })}
        className="admin-group-detail-tabs__nav"
        onKeyDown={handleTabListKeyDown}
        role="tablist"
        tabs
      >
        <NavItem role="presentation">
          <NavLink
            aria-controls="admin-group-detail-panel-group"
            aria-selected={activeTab === 'group'}
            className={classnames({ active: activeTab === 'group' })}
            id="admin-group-detail-tab-group"
            onClick={() => toggle('group')}
            role="tab"
            tabIndex={focusableTab === 'group' ? 0 : -1}
            tag="button"
            type="button"
          >
            <FormattedMessage id="components.pages.admin.group_details"/>
          </NavLink>
        </NavItem>
        {hasUsersTabAccess && <NavItem role="presentation">
          <NavLink
            aria-controls="admin-group-detail-panel-users"
            aria-selected={activeTab === 'users'}
            className={classnames({ active: activeTab === 'users' })}
            id="admin-group-detail-tab-users"
            onClick={() => toggle('users')}
            role="tab"
            tabIndex={focusableTab === 'users' ? 0 : -1}
            tag="button"
            type="button"
          >
            <FormattedMessage id="components.pages.admin.group_manage_users"/>
          </NavLink>
        </NavItem>}
        {hasPermissionsTabAccess && <NavItem role="presentation">
          <NavLink
            aria-controls="admin-group-detail-panel-acls"
            aria-selected={activeTab === 'acls'}
            className={classnames({ active: activeTab === 'acls' })}
            id="admin-group-detail-tab-acls"
            onClick={() => toggle('acls')}
            role="tab"
            tabIndex={focusableTab === 'acls' ? 0 : -1}
            tag="button"
            type="button"
          >
            <FormattedMessage id="components.pages.admin.group_details.manage_permissions"/>
          </NavLink>
        </NavItem>}
      </Nav>
      <TabContent activeTab={activeTab} className="admin-group-detail-tabs__content">
        <TabPane
          aria-hidden={activeTab !== 'group'}
          aria-labelledby="admin-group-detail-tab-group"
          className="admin-group-detail-tabs__panel admin-group-detail-tabs__panel--details"
          id="admin-group-detail-panel-group"
          role="tabpanel"
          tabId="group"
          tabIndex="0"
        >
          {tabs.group ? (
            <GroupEditForm
              group={group}
              toggleEditMode={toggleEditMode}
              updateGroup={updateGroup}
            />
          ) : (
            <GroupDetailsTab group={group} toggleEditMode={toggleEditMode}/>
          )}
        </TabPane>
        {hasUsersTabAccess && <TabPane
          aria-hidden={activeTab !== 'users'}
          aria-labelledby="admin-group-detail-tab-users"
          className="admin-group-detail-tabs__panel admin-group-detail-tabs__panel--users"
          id="admin-group-detail-panel-users"
          role="tabpanel"
          tabId="users"
          tabIndex="0"
        >
          <GroupUsersTab
            group={group}
            usersInGroup={usersInGroup}
            usersNotInGroup={usersNotInGroup}
            updateGroupWithUser={updateGroupWithUser}
          />
        </TabPane>}
        {hasPermissionsTabAccess && <TabPane
          aria-hidden={activeTab !== 'acls'}
          aria-labelledby="admin-group-detail-tab-acls"
          className="admin-group-detail-tabs__panel admin-group-detail-tabs__panel--permissions"
          id="admin-group-detail-panel-acls"
          role="tabpanel"
          tabId="acls"
          tabIndex="0"
        >
          <GroupPermissionsTab
            permissionsInGroup={permissionsInGroup}
            permissionsNotInGroup={permissionsNotInGroup}
            updatePermissions={updatePermissions}
            submitPermissions={submitPermissions}
          />
        </TabPane>}
      </TabContent>
    </div>
  );
}

GroupDetailsComponent.defaultProps = {};

const GroupDetails = connect(null, {})(GroupDetailsComponent);

export { GroupDetails, GroupDetailsComponent };
